import { normalizeSocialConnectionId } from './connection-id'

export const SOCIAL_WORKSPACE_SCHEMA_VERSION = 1

const MAX_DRAFT_LENGTH = 4_000
const MAX_DRAFTS = 50

export type SocialDockMode = 'expanded' | 'minimized'

export type SocialWorkspaceSnapshot = {
  dockMode: SocialDockMode
  activeConnectionId: string | null
  drafts: Readonly<Record<string, string>>
}

export type SocialDataEventType
  = | 'message.committed'
    | 'read.advanced'
    | 'relationship.changed'
    | 'conversation.notifications.changed'

export type SocialDataEvent = {
  type: SocialDataEventType
  connectionId?: string
  eventId: string
  originTabId: string
}

type Version = {
  timestamp: number
  sequence: number
  tabId: string
}

type Versioned<T> = {
  value: T
  version: Version
}

type PersistedWorkspace = {
  schemaVersion: typeof SOCIAL_WORKSPACE_SCHEMA_VERSION
  dockMode: Versioned<SocialDockMode>
  activeConnectionId: Versioned<string | null>
  drafts: Record<string, Versioned<string>>
}

type ChannelMessage
  = | { kind: 'workspace.patch', workspace: PersistedWorkspace }
    | { kind: 'data.event', event: SocialDataEvent }

export interface SocialWorkspaceStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

export interface SocialWorkspaceChannel {
  postMessage: (message: ChannelMessage) => void
  subscribe: (listener: (message: ChannelMessage) => void) => () => void
  close: () => void
}

export interface SocialStorageEvents {
  subscribe: (
    listener: (event: { key: string | null, newValue: string | null }) => void,
  ) => () => void
}

export type SocialWorkspaceStoreOptions = {
  userId: string
  storage?: SocialWorkspaceStorage
  channel?: SocialWorkspaceChannel
  storageEvents?: SocialStorageEvents
  now?: () => number
  tabId?: string
}

const EMPTY_VERSION: Version = {
  timestamp: 0,
  sequence: 0,
  tabId: '',
}

function makeEmptyWorkspace(): PersistedWorkspace {
  return {
    schemaVersion: SOCIAL_WORKSPACE_SCHEMA_VERSION,
    dockMode: { value: 'minimized', version: EMPTY_VERSION },
    activeConnectionId: { value: null, version: EMPTY_VERSION },
    drafts: {},
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readVersion(value: unknown): Version | null {
  if (!isRecord(value))
    return null
  const { timestamp, sequence, tabId } = value
  if (
    typeof timestamp !== 'number'
    || !Number.isFinite(timestamp)
    || timestamp < 0
    || typeof sequence !== 'number'
    || !Number.isInteger(sequence)
    || sequence < 0
    || typeof tabId !== 'string'
  ) {
    return null
  }
  return { timestamp, sequence, tabId }
}

function readVersioned<T>(
  value: unknown,
  isValue: (candidate: unknown) => candidate is T,
): Versioned<T> | null {
  if (!isRecord(value) || !isValue(value.value))
    return null
  const version = readVersion(value.version)
  return version ? { value: value.value, version } : null
}

function parseWorkspaceValue(value: unknown): PersistedWorkspace | null {
  if (
    !isRecord(value)
    || value.schemaVersion !== SOCIAL_WORKSPACE_SCHEMA_VERSION
  ) {
    return null
  }

  const dockMode = readVersioned(
    value.dockMode,
    (candidate): candidate is SocialDockMode =>
      candidate === 'expanded' || candidate === 'minimized',
  )
  const rawActiveConnectionId = readVersioned(
    value.activeConnectionId,
    (candidate): candidate is string | null =>
      candidate === null || typeof candidate === 'string',
  )
  if (!dockMode || !rawActiveConnectionId || !isRecord(value.drafts))
    return null
  const activeConnectionId: Versioned<string | null> = {
    ...rawActiveConnectionId,
    value: normalizeSocialConnectionId(rawActiveConnectionId.value),
  }

  const drafts: Record<string, Versioned<string>> = {}
  for (const [connectionId, draftValue] of Object.entries(value.drafts)) {
    if (!connectionId)
      continue
    const draft = readVersioned(
      draftValue,
      (candidate): candidate is string => typeof candidate === 'string',
    )
    if (draft)
      drafts[connectionId] = draft
  }

  return {
    schemaVersion: SOCIAL_WORKSPACE_SCHEMA_VERSION,
    dockMode,
    activeConnectionId,
    drafts,
  }
}

function parseWorkspace(raw: string | null): PersistedWorkspace | null {
  if (!raw)
    return null
  try {
    return parseWorkspaceValue(JSON.parse(raw))
  }
  catch {
    return null
  }
}

function compareVersions(left: Version, right: Version): number {
  if (left.timestamp !== right.timestamp)
    return left.timestamp - right.timestamp
  if (left.sequence !== right.sequence)
    return left.sequence - right.sequence
  return left.tabId.localeCompare(right.tabId)
}

function randomId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    return `${prefix}-${crypto.randomUUID()}`
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function workspaceSnapshot(
  workspace: PersistedWorkspace,
): SocialWorkspaceSnapshot {
  const drafts: Record<string, string> = {}
  for (const [connectionId, draft] of Object.entries(workspace.drafts)) {
    if (draft.value)
      drafts[connectionId] = draft.value
  }
  return {
    dockMode: workspace.dockMode.value,
    activeConnectionId: workspace.activeConnectionId.value,
    drafts,
  }
}

function parseDataEvent(raw: string | null): SocialDataEvent | null {
  if (!raw)
    return null
  try {
    const value: unknown = JSON.parse(raw)
    if (!isRecord(value))
      return null
    const { type } = value
    if (
      type !== 'message.committed'
      && type !== 'read.advanced'
      && type !== 'relationship.changed'
      && type !== 'conversation.notifications.changed'
    ) {
      return null
    }
    if (
      typeof value.eventId !== 'string'
      || typeof value.originTabId !== 'string'
      || (value.connectionId !== undefined
        && typeof value.connectionId !== 'string')
    ) {
      return null
    }
    return {
      type,
      eventId: value.eventId,
      originTabId: value.originTabId,
      ...(value.connectionId ? { connectionId: value.connectionId } : {}),
    }
  }
  catch {
    return null
  }
}

export class SocialWorkspaceStore {
  readonly storageKey: string
  readonly eventStorageKey: string

  private workspace: PersistedWorkspace
  private snapshot: SocialWorkspaceSnapshot
  private sequence = 0
  private readonly listeners = new Set<() => void>()
  private readonly dataListeners = new Set<(event: SocialDataEvent) => void>()
  private readonly seenEventIds = new Set<string>()
  private readonly unsubscribers: Array<() => void> = []
  private readonly now: () => number
  private readonly tabId: string

  constructor(private readonly options: SocialWorkspaceStoreOptions) {
    this.storageKey = `mentiss:social:v1:${options.userId}`
    this.eventStorageKey = `mentiss:social:event:v1:${options.userId}`
    this.now = options.now ?? Date.now
    this.tabId = options.tabId ?? randomId('tab')
    const storedWorkspace = this.readStoredWorkspace()
    this.workspace = storedWorkspace ?? makeEmptyWorkspace()
    this.snapshot = workspaceSnapshot(this.workspace)
    if (storedWorkspace) {
      try {
        options.storage?.setItem(
          this.storageKey,
          JSON.stringify(this.workspace),
        )
      }
      catch {}
    }

    if (options.channel) {
      this.unsubscribers.push(
        options.channel.subscribe(message =>
          this.receiveChannelMessage(message),
        ),
      )
    }
    if (options.storageEvents) {
      this.unsubscribers.push(
        options.storageEvents.subscribe(event =>
          this.receiveStorageEvent(event),
        ),
      )
    }
  }

  getSnapshot = (): SocialWorkspaceSnapshot => this.snapshot

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  subscribeToDataEvents = (
    listener: (event: SocialDataEvent) => void,
  ): (() => void) => {
    this.dataListeners.add(listener)
    return () => this.dataListeners.delete(listener)
  }

  setDockMode(mode: SocialDockMode): void {
    if (this.workspace.dockMode.value === mode)
      return
    this.workspace = {
      ...this.workspace,
      dockMode: { value: mode, version: this.nextVersion() },
    }
    this.commitWorkspace()
  }

  openConnection(connectionId: string | null): void {
    const normalizedConnectionId = normalizeSocialConnectionId(connectionId)
    if (this.workspace.activeConnectionId.value === normalizedConnectionId)
      return
    this.workspace = {
      ...this.workspace,
      activeConnectionId: {
        value: normalizedConnectionId,
        version: this.nextVersion(),
      },
    }
    this.commitWorkspace()
  }

  setDraft(connectionId: string, text: string): void {
    if (!connectionId)
      return
    const normalized = text.slice(0, MAX_DRAFT_LENGTH)
    if ((this.workspace.drafts[connectionId]?.value ?? '') === normalized)
      return

    const drafts = {
      ...this.workspace.drafts,
      [connectionId]: {
        value: normalized,
        version: this.nextVersion(),
      },
    }
    const entries = Object.entries(drafts)
    if (entries.length > MAX_DRAFTS) {
      entries
        .sort(([, left], [, right]) =>
          compareVersions(right.version, left.version),
        )
        .slice(MAX_DRAFTS)
        .forEach(([key]) => delete drafts[key])
    }

    this.workspace = { ...this.workspace, drafts }
    this.commitWorkspace()
  }

  clearDraft(connectionId: string): void {
    this.setDraft(connectionId, '')
  }

  clearDraftIfMatches(connectionId: string, expectedText: string): boolean {
    const storedWorkspace = this.readStoredWorkspace()
    if (storedWorkspace)
      this.mergeWorkspace(storedWorkspace)

    if ((this.workspace.drafts[connectionId]?.value ?? '') !== expectedText)
      return false
    this.clearDraft(connectionId)
    return true
  }

  publishDataEvent(type: SocialDataEventType, connectionId?: string): void {
    const event: SocialDataEvent = {
      type,
      connectionId,
      eventId: randomId('event'),
      originTabId: this.tabId,
    }
    this.rememberEvent(event.eventId)
    this.options.channel?.postMessage({ kind: 'data.event', event })
    try {
      this.options.storage?.setItem(
        this.eventStorageKey,
        JSON.stringify(event),
      )
      this.options.storage?.removeItem(this.eventStorageKey)
    }
    catch {}
  }

  clearPersistedState(): void {
    try {
      this.options.storage?.removeItem(this.storageKey)
      this.options.storage?.removeItem(this.eventStorageKey)
    }
    catch {}
    this.workspace = makeEmptyWorkspace()
    this.updateSnapshot()
  }

  destroy(): void {
    this.unsubscribers.splice(0).forEach(unsubscribe => unsubscribe())
    this.options.channel?.close()
    this.listeners.clear()
    this.dataListeners.clear()
  }

  private readStoredWorkspace(): PersistedWorkspace | null {
    try {
      return parseWorkspace(
        this.options.storage?.getItem(this.storageKey) ?? null,
      )
    }
    catch {
      return null
    }
  }

  private nextVersion(): Version {
    this.sequence += 1
    return {
      timestamp: this.now(),
      sequence: this.sequence,
      tabId: this.tabId,
    }
  }

  private commitWorkspace(): void {
    this.updateSnapshot()
    try {
      this.options.storage?.setItem(
        this.storageKey,
        JSON.stringify(this.workspace),
      )
    }
    catch {}
    this.options.channel?.postMessage({
      kind: 'workspace.patch',
      workspace: this.workspace,
    })
  }

  private updateSnapshot(): void {
    this.snapshot = workspaceSnapshot(this.workspace)
    this.listeners.forEach(listener => listener())
  }

  private receiveChannelMessage(message: ChannelMessage): void {
    if (message.kind === 'workspace.patch') {
      const incoming = parseWorkspaceValue(message.workspace)
      if (incoming)
        this.mergeWorkspace(incoming)
      return
    }
    this.receiveDataEvent(message.event)
  }

  private receiveStorageEvent(event: {
    key: string | null
    newValue: string | null
  }): void {
    if (event.key === this.storageKey) {
      if (event.newValue === null) {
        this.workspace = makeEmptyWorkspace()
        this.updateSnapshot()
        return
      }
      const incoming = parseWorkspace(event.newValue)
      if (incoming)
        this.mergeWorkspace(incoming)
      return
    }
    if (event.key === this.eventStorageKey) {
      const incoming = parseDataEvent(event.newValue)
      if (incoming)
        this.receiveDataEvent(incoming)
    }
  }

  private receiveDataEvent(event: SocialDataEvent): void {
    if (
      event.originTabId === this.tabId
      || this.seenEventIds.has(event.eventId)
    ) {
      return
    }
    this.rememberEvent(event.eventId)
    this.dataListeners.forEach(listener => listener(event))
  }

  private rememberEvent(eventId: string): void {
    this.seenEventIds.add(eventId)
    if (this.seenEventIds.size <= 100)
      return
    const oldest = this.seenEventIds.values().next().value
    if (typeof oldest === 'string')
      this.seenEventIds.delete(oldest)
  }

  private mergeWorkspace(incoming: PersistedWorkspace): void {
    let changed = false
    let { dockMode } = this.workspace
    let { activeConnectionId } = this.workspace
    const drafts = { ...this.workspace.drafts }

    if (compareVersions(incoming.dockMode.version, dockMode.version) > 0) {
      dockMode = incoming.dockMode
      changed = true
    }
    if (
      compareVersions(
        incoming.activeConnectionId.version,
        activeConnectionId.version,
      ) > 0
    ) {
      activeConnectionId = incoming.activeConnectionId
      changed = true
    }
    for (const [connectionId, draft] of Object.entries(incoming.drafts)) {
      const current = drafts[connectionId]
      if (!current || compareVersions(draft.version, current.version) > 0) {
        drafts[connectionId] = draft
        changed = true
      }
    }

    if (!changed)
      return
    this.workspace = {
      schemaVersion: SOCIAL_WORKSPACE_SCHEMA_VERSION,
      dockMode,
      activeConnectionId,
      drafts,
    }
    this.updateSnapshot()
    try {
      this.options.storage?.setItem(
        this.storageKey,
        JSON.stringify(this.workspace),
      )
    }
    catch {}
  }
}

function makeBrowserChannel(name: string): SocialWorkspaceChannel | undefined {
  if (typeof BroadcastChannel === 'undefined')
    return undefined
  const channel = new BroadcastChannel(name)
  return {
    postMessage: message => channel.postMessage(message),
    subscribe: (listener) => {
      const handleMessage = (event: MessageEvent<ChannelMessage>) =>
        listener(event.data)
      channel.addEventListener('message', handleMessage)
      return () => channel.removeEventListener('message', handleMessage)
    },
    close: () => channel.close(),
  }
}

function makeBrowserStorageEvents(): SocialStorageEvents | undefined {
  if (typeof window === 'undefined')
    return undefined
  return {
    subscribe: (listener) => {
      const handleStorage = (event: StorageEvent) => listener(event)
      window.addEventListener('storage', handleStorage)
      return () => window.removeEventListener('storage', handleStorage)
    },
  }
}

export function createBrowserSocialWorkspace(
  userId: string,
): SocialWorkspaceStore {
  return new SocialWorkspaceStore({
    userId,
    storage: typeof localStorage === 'undefined' ? undefined : localStorage,
    channel: makeBrowserChannel(`mentiss-social:v1:${userId}`),
    storageEvents: makeBrowserStorageEvents(),
  })
}
